/**
 * Color palette for Turn Connect applications.
 * Provides consistent colors across web and mobile platforms based on Style Guide.
 */
import { colorTokens } from './tokens';

// Keys whose value IS a shared semantic token read it from ./tokens (the one
// color source), so they cannot drift from it. Everything else here is the
// legacy palette, unchanged; new code should use the tokens directly.
const tk = colorTokens.dark;

export const colors = {
  // Background colors - Dark theme
  background: {
    primary: tk.bg, // Main background
    secondary: '#0B0B0B', // Cards, modals
    tertiary: '#1A1A1A', // Elevated surfaces
    quaternary: '#2A2A2A', // Form inputs, dropdowns
    elevated: '#333333', // Highest elevation
    active: '#2F5F3F', // Active elements
    inactive: '#d32f2f1a', // Inactive elements
    selected: '#0F192E', // Selected elements
    highlight: '#252525', // Highlighted rows, selections
    default: '#FFFFFF', // Light background for contrast
  },

  // Text colors - High contrast for dark theme
  text: {
    primary: tk.text.primary, // Main text
    secondary: '#CCCCCC', // Secondary text
    tertiary: '#999999', // Muted text, labels
    quaternary: '#666666', // Disabled text
    disabled: '#444444', // Very disabled
    inverted: '#000000', // Text on light backgrounds
    // Text sitting ON a filled accent surface (brand / success / error button).
    // White in BOTH themes: the button's own color is the ground, not the
    // page's, so this must not flip with the theme the way `inverted` does.
    onAccent: tk.onAccent,
    // Small text that flags attention (past due, over target). The brand
    // scarlet itself is 3.8:1 on white, below AA for body-size text, so
    // attention copy uses this per-theme shade instead (AA in both themes).
    accent: tk.textAccent,
    placeholder: '#EEEEEE', // Placeholder text
    selected: '#6BA6F3',
  },

  // Brand colors - Primary brand identity (Based on Style Guide)
  brand: {
    primary: tk.brand, // Main brand orange (Primary button color)
    secondary: '#1958C4', // Blue for secondary actions
    accent: '#FF9500', // Orange accent
    dark: '#C84826', // Darker orange for hover
    darker: '#B23E1F', // Darkest orange for active
    // Solid scarlet SURFACE under text (primary buttons, filled badges).
    // Deeper than `primary` so white `text.onAccent` clears AA (4.8:1) at any
    // size; `primary` stays the brand mark / accent colour.
    fill: tk.brandFill,
    fillHover: tk.brandFillPressed,
    fillActive: '#9C3519',
  },

  // Status colors - Semantic states
  status: {
    success: '#4CAF50', // Green - success, active
    successHover: '#45a049', // Darker green for hover
    error: '#DC2626', // Red - errors, delete (dark red for better contrast)
    errorHover: '#b91c1c', // Darker red for hover
    warning: '#FF9800', // Orange - warnings, pending
    warningHover: '#e68900', // Darker orange for hover
    info: '#2196F3', // Blue - information
    infoHover: '#1976D2', // Darker blue for hover
    neutral: '#6B7280', // Gray - neutral actions
  },

  // Interactive colors - UI interactions (Based on Style Guide)
  interactive: {
    primary: tk.brand, // Primary buttons, links (Orange)
    primaryHover: '#C84826', // Primary hover state (Darker orange)
    primaryActive: '#B23E1F', // Primary active state (Darkest orange)
    secondary: '#6B7280', // Secondary buttons (Gray)
    secondaryHover: '#4B5563', // Secondary hover
    secondaryActive: '#374151', // Secondary active
    disabled: '#444444', // Disabled state
    focus: '#007AFF', // Focus rings (Blue - Style Guide)
  },

  // Border colors - Consistent borders
  border: {
    default: '#2A2A1F', // Default borders - subtle, less visible
    light: '#333333', // Light borders
    lighter: '#444444', // Lighter borders
    focus: '#007AFF', // Focused borders (Blue - Style Guide)
    error: '#DC2626', // Error borders (dark red)
    success: '#4CAF50', // Success borders
    selected: '#1958C4', // Selected borders (Blue)
    required: '#FF9800', // Required field borders (Orange - highly visible)
  },

  // Overlay colors - Modals, tooltips, and surface tints.
  // The white "wash" scale (subtle→strong) is the dark-theme idiom for elevation
  // tints and hairlines; in light mode these flip to black at low alpha so the
  // same subtle wash reads on a light ground. One scale, reused everywhere.
  overlay: {
    backdrop: 'rgba(0, 0, 0, 0.8)', // Modal backdrop
    light: 'rgba(255, 255, 255, 0.1)', // Light overlay
    medium: 'rgba(255, 255, 255, 0.2)', // Medium overlay
    dark: 'rgba(0, 0, 0, 0.5)', // Dark overlay
    darker: 'rgba(0, 0, 0, 0.8)', // Darker overlay
    wash: 'rgba(255, 255, 255, 0.04)', // Subtlest surface tint / hover
    washStrong: 'rgba(255, 255, 255, 0.08)', // Card tint, list separators
    hairline: 'rgba(255, 255, 255, 0.14)', // Divider / faint border
    scrim: 'rgba(255, 255, 255, 0.28)', // Strong tint, chip on surface
  },

  // Work order specific colors
  workOrder: {
    completed: '#4CAF50', // Green
    inProgress: '#FF9800', // Orange
    pending: '#2196F3', // Blue
    cancelled: '#DC2626', // Red (dark red)
    onHold: '#9C27B0', // Purple
    draft: '#6B7280', // Gray
  },

  // User status colors
  userStatus: {
    active: '#4CAF50', // Green
    inactive: '#666666', // Gray
    suspended: '#DC2626', // Red (dark red)
    pending: '#FF9800', // Orange
    invited: '#2196F3', // Blue
  },

  // Location status colors
  locationStatus: {
    active: '#4CAF50', // Green
    inactive: '#666666', // Gray
    maintenance: '#FF9800', // Orange
    closed: '#DC2626', // Red (dark red)
    opening: '#2196F3', // Blue
  },

  // Severity colors - For alerts, notifications
  severity: {
    critical: '#DC2626', // Red (dark red)
    high: '#FF9800', // Orange
    medium: '#FFD700', // Yellow
    low: '#4CAF50', // Green
    info: '#2196F3', // Blue
  },

  // Chart colors - For data visualization
  chart: {
    primary: '#007AFF',
    secondary: '#5AC8FA',
    tertiary: '#FF9500',
    quaternary: '#4CAF50',
    quinary: '#DC2626', // Red (dark red)
    senary: '#9C27B0',
    gradient: {
      blue: 'linear-gradient(135deg, #007AFF 0%, #5AC8FA 100%)',
      green: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
      orange: 'linear-gradient(135deg, #FF9500 0%, #FFB74D 100%)',
      red: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
    },
  },
};

export type Colors = typeof colors;
